import type { CampaignSpreadsheetViewType } from '../context/CampaignContext';
import { getUserSessionCookie } from '../utils/SessionCookie';
import { apiFetch } from './ApiFetch';
import { BASE_URL } from './constants';
import type { EnrichingCellItem } from './spreadsheet';
import {
  QUALIFIED_COLUMN_NAME,
  REASON_COLUMN_NAME,
  SCORE_COLUMN_NAME,
  TIER_COLUMN_NAME
} from './spreadsheet';

/** Appends view_type query param to URL when provided. */
function withViewTypeQueryParam(
  url: string,
  viewType?: CampaignSpreadsheetViewType
): string {
  if (!viewType) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}view_type=${encodeURIComponent(viewType)}`;
}

/** Backend prioritization types - matches Python PrioritizationType enum. */
export enum PrioritizationType {
  TIERING = 'tiering',
  QUALIFYING = 'qualifying',
  SCORING = 'scoring'
}

/** Single tier definition - backend TierDefinition. */
export interface TierDefinition {
  label: string;
  criteria: string;
}

/** Tiering config - backend TieringConfig. */
export interface TieringConfig {
  tiers: TierDefinition[];
}

/** Qualifying config - backend QualifyingConfig. */
export interface QualifyingConfig {
  qualified_criteria: string;
  unqualified_criteria: string;
}

/** Scoring config - backend ScoringConfig. */
export interface ScoringConfig {
  criteria: string;
}

/** Request payload for tiering prioritization. */
export interface CampaignPrioritizationTiering {
  prioritization_type: PrioritizationType.TIERING;
  config: TieringConfig;
}

/** Request payload for qualifying prioritization. */
export interface CampaignPrioritizationQualifying {
  prioritization_type: PrioritizationType.QUALIFYING;
  config: QualifyingConfig;
}

/** Request payload for scoring prioritization. */
export interface CampaignPrioritizationScoring {
  prioritization_type: PrioritizationType.SCORING;
  config: ScoringConfig;
}

/** Union of all prioritization request payloads. */
export type CampaignPrioritizationRequest =
  | CampaignPrioritizationTiering
  | CampaignPrioritizationQualifying
  | CampaignPrioritizationScoring;

/** API response - backend CampaignPrioritizationResponse. */
export interface CampaignPrioritizationResponse {
  prioritization_type: string;
  config: Record<string, unknown>;
}

/** GET response wraps prioritization in `prioritization` key. */
export interface GetCampaignPrioritizationResponse {
  prioritization: CampaignPrioritizationResponse | null;
}

/** PUT response wraps prioritization in `prioritization` key. */
export interface UpsertCampaignPrioritizationResponse {
  prioritization: CampaignPrioritizationResponse;
}

const PRIORITIZATION_BASE = `${BASE_URL}/v1/campaigns`;

export async function getCampaignPrioritization(
  campaignId: string,
  viewType?: CampaignSpreadsheetViewType
): Promise<GetCampaignPrioritizationResponse> {
  const userSessionToken = getUserSessionCookie();
  const url = withViewTypeQueryParam(
    `${PRIORITIZATION_BASE}/${campaignId}/prioritization`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

export async function upsertCampaignPrioritization(
  campaignId: string,
  data: CampaignPrioritizationRequest,
  viewType?: CampaignSpreadsheetViewType
): Promise<UpsertCampaignPrioritizationResponse> {
  const userSessionToken = getUserSessionCookie();
  const url = withViewTypeQueryParam(
    `${PRIORITIZATION_BASE}/${campaignId}/prioritization`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    },
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

/** Start/run prioritization on campaign prospects. Triggers the prioritization job. */
export async function startCampaignPrioritization(
  campaignId: string,
  viewType?: CampaignSpreadsheetViewType
): Promise<{ job_id?: string; message?: string }> {
  const userSessionToken = getUserSessionCookie();
  const url = withViewTypeQueryParam(
    `${PRIORITIZATION_BASE}/${campaignId}/prioritization/run`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

/** Request body for POST prioritization/execute. */
export interface StartPrioritizationRequest {
  row_ids: number[];
}

/** Response from POST prioritization/execute. */
export interface StartPrioritizationResponse {
  workflow_id: string;
}

/** Response from GET prioritization/execute/active-workflow-id (backend: 200 when workflow is non-terminal; 404 when none). */
export interface ActiveProspectPrioritizationWorkflowIdResponse {
  workflow_id: string;
}

/**
 * Returns the active prospect prioritization workflow id, or null if none (404).
 * Backend: `GET /v1/campaigns/{campaign_id}/prioritization/execute/active-workflow-id` (+ view_type).
 */
export async function getActiveProspectPrioritizationWorkflowId(
  campaignId: string,
  viewType?: CampaignSpreadsheetViewType
): Promise<ActiveProspectPrioritizationWorkflowIdResponse | null> {
  const userSessionToken = getUserSessionCookie();
  const url = withViewTypeQueryParam(
    `${PRIORITIZATION_BASE}/${campaignId}/prioritization/execute/active-workflow-id`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

/**
 * In-flight prioritization cells for UI highlight (same shape as enrichment).
 * Backend: `GET /v1/campaigns/{campaign_id}/prioritization/execute/{workflow_id}/prioritizing` (+ view_type).
 */
export async function getPrioritizingCells(
  campaignId: string,
  workflowId: string,
  viewType?: CampaignSpreadsheetViewType
): Promise<EnrichingCellItem[]> {
  const userSessionToken = getUserSessionCookie();
  const encodedWorkflowId = encodeURIComponent(workflowId);
  const url = withViewTypeQueryParam(
    `${PRIORITIZATION_BASE}/${campaignId}/prioritization/execute/${encodedWorkflowId}/prioritizing`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  return data?.cells_prioritizing ?? [];
}

/** Column names written by prioritization for each method (for in-sheet highlight while running). */
export function getPrioritizationOutputColumnNames(
  prioritizationType: PrioritizationType
): string[] {
  switch (prioritizationType) {
    case PrioritizationType.TIERING:
      return [TIER_COLUMN_NAME, REASON_COLUMN_NAME];
    case PrioritizationType.QUALIFYING:
      return [QUALIFIED_COLUMN_NAME, REASON_COLUMN_NAME];
    case PrioritizationType.SCORING:
      return [SCORE_COLUMN_NAME, REASON_COLUMN_NAME];
    default:
      return [];
  }
}

export async function startProspectPrioritizationExecute(
  campaignId: string,
  request: StartPrioritizationRequest,
  viewType?: CampaignSpreadsheetViewType
): Promise<StartPrioritizationResponse> {
  const userSessionToken = getUserSessionCookie();
  const url = withViewTypeQueryParam(
    `${PRIORITIZATION_BASE}/${campaignId}/prioritization/execute`,
    viewType
  );
  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    },
    body: JSON.stringify(request)
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
}

/** Status event from prioritization SSE stream. */
export type PrioritizationStatus = 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface PrioritizationStreamEvent {
  status?: PrioritizationStatus;
  error?: string;
}

/** Callbacks for prioritization SSE stream. */
export interface PrioritizationStreamCallbacks {
  onStatus: (status: PrioritizationStatus, error?: string) => void;
  onComplete: () => void;
  onError: (message: string) => void;
}

/** Subscribe to SSE stream of prioritization status. Uses fetch so auth header is sent. */
export function streamProspectPrioritizationResult(
  campaignId: string,
  workflowId: string,
  callbacks: PrioritizationStreamCallbacks,
  viewType?: CampaignSpreadsheetViewType
): () => void {
  const userSessionToken = getUserSessionCookie();
  const encodedWorkflowId = encodeURIComponent(workflowId);
  const baseUrl = `${PRIORITIZATION_BASE}/${campaignId}/prioritization/execute/${encodedWorkflowId}/stream`;
  const url = withViewTypeQueryParam(baseUrl, viewType);
  let cancelled = false;
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  (async () => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${userSessionToken}`
        }
      });
      if (!response.ok) {
        callbacks.onError(`Stream failed: ${response.status}`);
        return;
      }
      reader = response.body?.getReader() ?? null;
      if (!reader) {
        callbacks.onError('No response body');
        return;
      }
      const decoder = new TextDecoder();
      let buffer = '';
      while (!cancelled) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data) continue;
          try {
            const parsed = JSON.parse(data) as PrioritizationStreamEvent;
            if (parsed?.status) {
              callbacks.onStatus(parsed.status, parsed.error);
              if (parsed.status === 'COMPLETED' || parsed.status === 'FAILED') {
                callbacks.onComplete();
                return;
              }
            }
          } catch {
            // ignore non-JSON or unexpected lines
          }
        }
      }
      if (!cancelled) {
        callbacks.onComplete();
      }
    } catch (err) {
      if (!cancelled) {
        callbacks.onError(err instanceof Error ? err.message : 'Stream error');
      }
    }
  })();

  return () => {
    cancelled = true;
    if (reader) {
      reader.cancel().catch(() => {});
    }
  };
}
